import React, { useState, useEffect } from 'react';
import {
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Coins,
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Trash2,
  RefreshCw,
  Package,
  Check,
  X,
  Info,
  Copy,
  ExternalLink,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Settings,
  Menu
} from 'lucide-react';
import { useWallet } from "../contexts/WalletContext";
import { bundlerService } from '../services/frontend-bundler/bundler.service';
import { Connection, Keypair } from '@solana/web3.js';
import { tradingService } from '../services/frontend-bundler/trading.service';
import { VersionedTransaction } from '@solana/web3.js';

interface TokenHolding {
  walletAddress: string;
  walletIndex: number;
  balance: number;
  uiBalance: number;
  value: number;
}

interface PortfolioEntry {
  mint: string;
  symbol: string;
  name: string;
  totalBalance: number;
  decimals: number;
  currentPrice: number;
  totalValue: number;
  avgBuyPrice: number;
  totalInvested: number;
  pnl: number;
  pnlPercentage: number;
  priceChange24h?: number;
  image?: string;
  walletHoldings: TokenHolding[];
}

interface TokenPortfolio {
  [mint: string]: PortfolioEntry;
}

interface BatchSellSettings {
  enabled: boolean;
  batchSize: number;
  delayBetweenBatches: number;
  slippageBps: number;
}

interface TokenMetadata {
  symbol: string;
  name: string;
  logoURI?: string;
  decimals: number;
  tags?: string[];
  extensions?: Record<string, any>;
}

interface TokenPrice {
  price: number;
  vsToken?: string;
  confidence?: number;
  priceChange24h?: number;
  volume24h?: number;
  marketCap?: number;
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

const BundlerTokenPortfolioManager: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  
  // Bundle management
  const [bundles, setBundles] = useState<any[]>([]);
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [currentBundle, setCurrentBundle] = useState<any>(null);
  
  // Portfolio state
  const [wallets, setWallets] = useState<any[]>([]);
  const [tokenPortfolio, setTokenPortfolio] = useState<TokenPortfolio>({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sellAmount, setSellAmount] = useState<{ [key: string]: string }>({});
  const [selling, setSelling] = useState<{ [key: string]: boolean }>({});
  const [expandedWallets, setExpandedWallets] = useState<{ [key: string]: boolean }>({});
  
  // UI state
  const [showPrices, setShowPrices] = useState(true);
  const [sortBy, setSortBy] = useState<'value' | 'pnl' | 'change'>('value');
  const [filterPositive, setFilterPositive] = useState<boolean | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Data caches
  const [purchaseHistory, setPurchaseHistory] = useState<{ [mint: string]: any }>({});
  const [solPrice, setSolPrice] = useState<number>(100);
  const [tokenMetadataCache, setTokenMetadataCache] = useState<Map<string, TokenMetadata>>(new Map());
  
  // Batch settings
  const [showBatchSettings, setShowBatchSettings] = useState(false);
  const [batchSettings, setBatchSettings] = useState<BatchSellSettings>({
    enabled: true,
    batchSize: 10,
    delayBetweenBatches: 100,
    slippageBps: 1000
  });

const fetchPumpFunData = async (mints: string[]) => {
  const pumpData = new Map();
  
  for (const mint of mints) {
    try {
      const response = await fetch(`${API_BASE}/pumpfun/${mint}`);
      
      if (response.ok) {
        const data = await response.json();
        pumpData.set(mint, {
          symbol: data.symbol || data.name?.slice(0, 4),
          name: data.name || 'Pump.fun Token',
          image: data.image_uri,
          price: data.price || 0,
          marketCap: data.usd_market_cap,
          decimals: 6
        });
        console.log(`✅ Pump.fun: ${data.symbol} = $${data.price}`);
      } else {
        // Not a pump.fun token or API down - skip silently
        console.log(`⚠️ ${mint} not on pump.fun or API unavailable`);
      }
    } catch (err) {
      console.log(`⚠️ Pump.fun fetch failed for ${mint}`);
    }
    
    // Delay between requests
    await new Promise(resolve => setTimeout(resolve, 300));
  }
  
  return pumpData;
};

  // Add near the top of your component
const isPumpFunToken = async (mintAddress: string): Promise<boolean> => {
  try {
    // Check if token exists on pump.fun
    const response = await fetch(`https://frontend-api.pump.fun/coins/${mintAddress}`);
    return response.ok;
  } catch {
    return false;
  }
};

// Cache pump.fun token status
const [pumpFunTokens, setPumpFunTokens] = useState<Set<string>>(new Set());

// Check all tokens when portfolio loads
useEffect(() => {
  const checkPumpFunTokens = async () => {
    const mints = Object.keys(tokenPortfolio);
    const checks = await Promise.all(
      mints.map(async (mint) => ({
        mint,
        isPump: await isPumpFunToken(mint)
      }))
    );
    
    const pumpMints = new Set(
      checks.filter(c => c.isPump).map(c => c.mint)
    );
    setPumpFunTokens(pumpMints);
    
    console.log('Detected pump.fun tokens:', Array.from(pumpMints));
  };
  
  if (Object.keys(tokenPortfolio).length > 0) {
    checkPumpFunTokens();
  }
}, [tokenPortfolio]);

  // Initialize
  useEffect(() => {
    loadPurchaseHistory();
    loadBatchSettings();
  }, []);

  // Load bundles when wallet connects
  useEffect(() => {
    if (isConnected && publicKey) {
      loadUserBundles();
    } else {
      setBundles([]);
      setSelectedBundleId(null);
      setCurrentBundle(null);
      setWallets([]);
    }
  }, [isConnected, publicKey]);

  // Fetch balances when wallets change
  useEffect(() => {
    if (wallets.length > 0) {
      fetchAllTokenBalances();
    }
  }, [wallets]);

  const loadUserBundles = async () => {
    if (!publicKey) return;

    try {
      const owner = publicKey.toString();
      const userBundles = await bundlerService.getUserBundles(owner);
      setBundles(userBundles);

      if (userBundles.length > 0) {
        const savedBundleId = localStorage.getItem('currentBundleId') || 
                             localStorage.getItem('current_bundle_id');
        const bundleToSelect = userBundles.find((b: any) => b.id === savedBundleId) || userBundles[0];
        
        setSelectedBundleId(bundleToSelect.id);
        setCurrentBundle(bundleToSelect);
        localStorage.setItem('currentBundleId', bundleToSelect.id);
        
        const bundleWallets = bundleToSelect.wallets.map((w: any) => ({
          address: w.address,
          balance: w.balance || 0,
          funded: w.funded || false,
          active: w.active || false
        }));
        setWallets(bundleWallets);
      }
    } catch (error) {
      console.error('Failed to load bundles:', error);
    }
  };

  const handleBundleSelect = async (bundleId: string) => {
    const bundle = bundles.find(b => b.id === bundleId);
    if (!bundle) return;

    setSelectedBundleId(bundleId);
    setCurrentBundle(bundle);
    localStorage.setItem('currentBundleId', bundleId);
    
    const bundleWallets = bundle.wallets.map((w: any) => ({
      address: w.address,
      balance: w.balance || 0,
      funded: w.funded || false,
      active: w.active || false
    }));
    setWallets(bundleWallets);
    
    await fetchAllTokenBalances();
  };

  const loadBatchSettings = () => {
    const saved = localStorage.getItem('batch_sell_settings');
    if (saved) {
      try {
        setBatchSettings(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load batch settings:', error);
      }
    }
  };

  const saveBatchSettings = (settings: BatchSellSettings) => {
    setBatchSettings(settings);
    localStorage.setItem('batch_sell_settings', JSON.stringify(settings));
  };

  const loadPurchaseHistory = () => {
    const history = localStorage.getItem('bundler_purchase_history');
    if (history) {
      try {
        setPurchaseHistory(JSON.parse(history));
      } catch (error) {
        console.error('Failed to parse purchase history:', error);
      }
    }
  };

  const savePurchaseHistory = (tokenMint: string, data: any) => {
    const newHistory = {
      ...purchaseHistory,
      [tokenMint]: {
        ...purchaseHistory[tokenMint],
        ...data,
        lastUpdated: Date.now()
      }
    };
    setPurchaseHistory(newHistory);
    localStorage.setItem('bundler_purchase_history', JSON.stringify(newHistory));
  };

  const fetchAllTokenBalances = async () => {
    if (wallets.length === 0) return;

    setLoading(true);
    try {
      const portfolio: TokenPortfolio = {};
      const HELIUS_KEY = process.env.REACT_APP_SOLANA_RPC_URL?.split('-')[1] || '37327d2d-9742-4fc4-b0d0-57d489b78697';

      await fetchTokensAlternativeMethod(wallets, portfolio, HELIUS_KEY);

      if (Object.keys(portfolio).length > 0) {
        await fetchTokenMetadataBatch(Object.keys(portfolio), portfolio);
      }

      const tokensNeedingPrice = Object.keys(portfolio).filter(
        (m) => !portfolio[m].currentPrice || portfolio[m].currentPrice === 0
      );
      
      if (tokensNeedingPrice.length > 0) {
        const prices = await fetchTokenPrices(tokensNeedingPrice);
        prices.forEach((p, mint) => {
          if (portfolio[mint]) {
            portfolio[mint].currentPrice = p.price ?? 0;
            if (typeof p.priceChange24h === 'number') {
              portfolio[mint].priceChange24h = p.priceChange24h;
            }
          }
        });
      }

      for (const mint in portfolio) {
        const token = portfolio[mint];
        const uiBalance = token.totalBalance / Math.pow(10, token.decimals || 9);
        token.totalValue = uiBalance * (token.currentPrice || 0);
        token.walletHoldings.forEach((h) => {
          h.value = h.uiBalance * (token.currentPrice || 0);
        });
      }

      calculatePnL(portfolio);
      setTokenPortfolio(portfolio);
    } catch (err) {
      console.error('Failed to fetch token balances:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTokensAlternativeMethod = async (walletsIn: any[], portfolio: TokenPortfolio, apiKey: string) => {
    const TOKEN_PROGRAMS = [
      { id: 'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA', name: 'SPL' },
      { id: 'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb', name: 'Token-2022' }
    ];

    for (let i = 0; i < walletsIn.length; i++) {
      const wallet = walletsIn[i];
      const walletAddress = wallet.address || wallet.publicKey;
      if (!walletAddress) continue;

      for (const program of TOKEN_PROGRAMS) {
        try {
          const response = await fetch(`https://mainnet.helius-rpc.com/?api-key=${apiKey}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jsonrpc: '2.0',
              id: 'token-accounts',
              method: 'getTokenAccountsByOwner',
              params: [
                walletAddress,
                { programId: program.id },
                { encoding: 'jsonParsed' }
              ]
            })
          });

          if (!response.ok) continue;
          const data = await response.json();

          const items = data?.result?.value || [];
          for (const account of items) {
            const info = account?.account?.data?.parsed?.info;
            if (!info) continue;

            const mint: string = info.mint;
            const tok = info.tokenAmount;
            const amount = parseFloat(tok?.amount || '0');
            if (amount <= 0) continue;

            const decimals = typeof tok?.decimals === 'number' ? tok.decimals : 9;
            const uiAmount = typeof tok?.uiAmount === 'number'
              ? tok.uiAmount
              : amount / Math.pow(10, decimals);

            if (!portfolio[mint]) {
              portfolio[mint] = {
                mint,
                symbol: 'Unknown',
                name: 'Unknown Token',
                totalBalance: 0,
                decimals,
                currentPrice: 0,
                totalValue: 0,
                avgBuyPrice: 0,
                totalInvested: 0,
                pnl: 0,
                pnlPercentage: 0,
                walletHoldings: [],
                image: undefined
              };
            }

            const exists = portfolio[mint].walletHoldings.find(h => h.walletAddress === walletAddress);
            if (!exists) {
              portfolio[mint].totalBalance += amount;
              portfolio[mint].walletHoldings.push({
                walletAddress,
                walletIndex: i,
                balance: amount,
                uiBalance: uiAmount,
                value: 0
              });
            }
          }
        } catch (err) {
          console.error(`Error fetching ${program.name} accounts for wallet ${i}:`, err);
        }
      }
    }
  };

  

  const fetchTokenMetadataBatch = async (mints: string[], portfolio: TokenPortfolio) => {
  const unseen = mints.filter(m => !tokenMetadataCache.has(m));
  const nextCache = new Map(tokenMetadataCache);

  const apply = (mint: string, meta: TokenMetadata) => {
    if (!portfolio[mint]) return;
    portfolio[mint].symbol = meta.symbol || portfolio[mint].symbol || mint.slice(0, 4).toUpperCase();
    portfolio[mint].name = meta.name || portfolio[mint].name || 'Unknown Token';
    portfolio[mint].decimals = typeof meta.decimals === 'number' ? meta.decimals : (portfolio[mint].decimals || 9);
    portfolio[mint].image = meta.logoURI || portfolio[mint].image;
  };

  // ✅ FETCH PUMP.FUN DATA FIRST - BEFORE EVERYTHING ELSE
  if (unseen.length > 0) {
    console.log('Fetching pump.fun data for', unseen.length, 'tokens');
    const pumpData = await fetchPumpFunData(unseen);
    
    for (const mint of unseen) {
      const data = pumpData.get(mint);
      if (data) {
        console.log(`Found pump.fun data for ${mint}:`, data);
        const meta: TokenMetadata = {
          symbol: data.symbol,
          name: data.name,
          logoURI: data.image,
          decimals: data.decimals
        };
        nextCache.set(mint, meta);
        apply(mint, meta);
        
        // ✅ ALSO UPDATE PRICE IMMEDIATELY
        if (portfolio[mint] && data.price) {
          portfolio[mint].currentPrice = data.price;
          console.log(`Set price for ${mint}: $${data.price}`);
        }
      }
    }
  }

  // Batch fetch from Jupiter API directly with proper typing
  if (unseen.length > 0) {
    try {
      const jupiterTokensResponse = await fetch('https://cache.jup.ag/tokens');
      if (jupiterTokensResponse.ok) {
        const allTokens: any[] = await jupiterTokensResponse.json();
        const tokenMap = new Map<string, any>();
        
        // Build map with proper type checking
        allTokens.forEach((token: any) => {
          if (token && token.address) {
            tokenMap.set(token.address, token);
          }
        });
        
        for (const mint of unseen) {
          const tokenInfo = tokenMap.get(mint);
          if (tokenInfo && typeof tokenInfo === 'object') {
            const meta: TokenMetadata = {
              symbol: tokenInfo.symbol || mint.slice(0, 4).toUpperCase(),
              name: tokenInfo.name || 'Unknown Token',
              logoURI: tokenInfo.logoURI || undefined,
              decimals: typeof tokenInfo.decimals === 'number' ? tokenInfo.decimals : 9,
              tags: Array.isArray(tokenInfo.tags) ? tokenInfo.tags : [],
              extensions: tokenInfo.extensions || {}
            };
            nextCache.set(mint, meta);
            apply(mint, meta);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch Jupiter token list:', error);
    }
  }

// Backend proxy with better error handling
for (const mint of unseen) {
  if (nextCache.has(mint)) continue;
  
  try {
    const r = await fetch(`${API_BASE}/token-metadata/${mint}`, {
      headers: { 
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (r.ok) {
      const t = await r.json();
      const meta: TokenMetadata = {
        symbol: t.symbol || mint.slice(0, 4).toUpperCase(),
        name: t.name || 'Unknown Token', 
        logoURI: t.logoURI || t.logoUri || t.image || t.logo,  // Check all possible fields
        decimals: typeof t.decimals === 'number' ? t.decimals : 9,
        tags: Array.isArray(t.tags) ? t.tags : [],
        extensions: t.extensions || {}
      };
      nextCache.set(mint, meta);
      apply(mint, meta);
    }
  } catch (error) {
    console.error(`Backend metadata fetch failed for ${mint}:`, error);
  }
}

  // Helius API with better response handling
  for (const mint of unseen) {
    if (nextCache.has(mint)) continue;
    
    try {
      const HELIUS_KEY = process.env.REACT_APP_SOLANA_RPC_URL?.split('-')[1] || 'qsRlXQCo2krq8UHETdJhwOPRdQSdiQCO';
      const heliusResponse = await fetch(`https://api.helius.xyz/v0/token-metadata?api-key=${HELIUS_KEY}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          mintAccounts: [mint],
          includeOffChain: true,
          disableCache: false
        })
      });
      
      if (heliusResponse.ok) {
        const heliusData = await heliusResponse.json();
        if (Array.isArray(heliusData) && heliusData.length > 0) {
          const tokenData = heliusData[0];
          const offChainData = tokenData?.offChainMetadata?.metadata;
          const onChainData = tokenData?.onChainMetadata?.metadata;
          const accountData = tokenData?.account?.data;
          
          const meta: TokenMetadata = {
            symbol: offChainData?.symbol || onChainData?.symbol || accountData?.symbol || mint.slice(0, 4).toUpperCase(),
            name: offChainData?.name || onChainData?.name || accountData?.name || 'Unknown Token',
            logoURI: offChainData?.image || onChainData?.image || accountData?.uri,
            decimals: accountData?.decimals || 9
          };
          nextCache.set(mint, meta);
          apply(mint, meta);
        }
      }
    } catch (error) {
      console.error(`Helius metadata fetch failed for ${mint}:`, error);
    }
  }

  // Try direct token list from Jupiter CDN (often more reliable)
  for (const mint of unseen) {
    if (nextCache.has(mint)) continue;
    
    try {
      const response = await fetch(`https://cdn.jsdelivr.net/gh/jup-ag/token-list@latest/solana.tokenlist.json`);
      if (response.ok) {
        const tokenList = await response.json();
        const token = tokenList.tokens?.find((t: any) => t.address === mint);
        if (token) {
          const meta: TokenMetadata = {
            symbol: token.symbol || mint.slice(0, 4).toUpperCase(),
            name: token.name || 'Unknown Token',
            logoURI: token.logoURI,
            decimals: token.decimals || 9,
            tags: token.tags || [],
            extensions: token.extensions || {}
          };
          nextCache.set(mint, meta);
          apply(mint, meta);
        }
      }
    } catch (_) {}
  }

  // SolanaFM fallback
  for (const mint of unseen) {
    if (nextCache.has(mint)) continue;
    
    try {
      const r = await fetch(`https://api.solana.fm/v1/tokens/${mint}`, { 
        headers: { Accept: 'application/json' } 
      });
      if (r.ok) {
        const d = await r.json();
        const meta: TokenMetadata = {
          symbol: d.symbol || mint.slice(0, 4).toUpperCase(),
          name: d.name || 'Unknown Token',
          logoURI: d.logoUri || d.logo,
          decimals: typeof d.decimals === 'number' ? d.decimals : 9
        };
        nextCache.set(mint, meta);
        apply(mint, meta);
      }
    } catch (_) {}
  }

  // Final fallback
  for (const mint of unseen) {
    if (!nextCache.has(mint)) {
      const meta: TokenMetadata = {
        symbol: mint.slice(0, 4).toUpperCase(),
        name: 'Unknown Token',
        decimals: portfolio[mint]?.decimals ?? 9
      };
      nextCache.set(mint, meta);
      apply(mint, meta);
    }
  }

  setTokenMetadataCache(nextCache);
};

  const fetchTokenPrices = async (mintAddresses: string[]): Promise<Map<string, TokenPrice>> => {
    const prices = new Map<string, TokenPrice>();
    try {
      const uniqueMints = Array.from(new Set(mintAddresses));

      try {
        const response = await fetch(`${API_BASE}/token-prices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
          },
          body: JSON.stringify({ mints: uniqueMints })
        });
        if (response.ok) {
          const data = await response.json();
          Object.entries<any>(data).forEach(([mint, priceObj]) => {
            prices.set(mint, {
              price: priceObj.price || 0,
              vsToken: priceObj.vsToken || 'USDC',
              confidence: priceObj.confidence,
              priceChange24h: priceObj.priceChange24h,
              volume24h: priceObj.volume24h,
              marketCap: priceObj.marketCap
            });
          });
        }
      } catch (_) {}

      if (prices.size === 0) {
        try {
          const cgResponse = await fetch(
            'https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd&include_24hr_change=true'
          );
          if (cgResponse.ok) {
            const cgData = await cgResponse.json();
            if (cgData.solana) {
              setSolPrice(cgData.solana.usd || 100);
              prices.set('So11111111111111111111111111111111111111112', {
                price: cgData.solana.usd,
                vsToken: 'USD',
                priceChange24h: cgData.solana.usd_24h_change
              });
            }
          }
        } catch (err) {
          console.error('CoinGecko API failed:', err);
          setSolPrice(100);
        }
      }

      for (const mint of uniqueMints) {
        if (!prices.has(mint)) {
          try {
            const r = await fetch(`${API_BASE}/quote-price/${mint}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` }
            });
            if (r.ok) {
              const d = await r.json();
              prices.set(mint, { price: d.price || 0, vsToken: 'USDC', confidence: 0.9 });
            }
          } catch (_) {}
        }
      }
    } catch (error) {
      console.error('Error fetching token prices:', error);
    }
    return prices;
  };

  const calculatePnL = (portfolio: TokenPortfolio) => {
    for (const mint in portfolio) {
      const t = portfolio[mint];
      const uiBal = t.totalBalance / Math.pow(10, t.decimals || 9);
      const currentValue = uiBal * (t.currentPrice || 0);

      const ph = purchaseHistory[mint] || {};
      const totalInvested = typeof ph.totalInvested === 'number' ? ph.totalInvested : 0;
      const avgBuyPrice = typeof ph.avgPrice === 'number' ? ph.avgPrice
                        : (uiBal > 0 && totalInvested > 0 ? totalInvested / uiBal : 0);

      t.totalInvested = totalInvested;
      t.avgBuyPrice = avgBuyPrice;
      t.totalValue = currentValue;
      t.pnl = currentValue - totalInvested;
      t.pnlPercentage = totalInvested > 0 ? (t.pnl / totalInvested) * 100 : 0;
    }
  };

 


// Updated handleSellToken function
const handleSellToken = async (tokenMint: string, sellAll: boolean = false, specificWallet?: string) => {
  if (!isConnected || !publicKey) {
    alert("Please connect your wallet first");
    return;
  }

  const sellKey = specificWallet ? `${tokenMint}-${specificWallet}` : tokenMint;
  setSelling(prev => ({ ...prev, [sellKey]: true }));

  try {
    const token = tokenPortfolio[tokenMint];
    
    if (!currentBundle) {
      alert("No active bundle found. Please select a bundle first.");
      return;
    }

    // Check if this is a pump.fun token
    const isPumpToken = pumpFunTokens.has(tokenMint);
    console.log(`Selling ${token.symbol} - Pump.fun token: ${isPumpToken}`);

    // Initialize connection
    const connection = new Connection(
      process.env.REACT_APP_SOLANA_RPC_URL || 'https://mainnet.helius-rpc.com/?api-key=37327d2d-9742-4fc4-b0d0-57d489b78697'
    );
    
    // Only initialize Jupiter trader if not a pump token
    const trader = !isPumpToken ? tradingService(connection) : null;

    // Get decrypted wallets
    const decryptedWallets = await bundlerService.getDecryptedWallets(currentBundle.id);
    if (!decryptedWallets || decryptedWallets.length === 0) {
      throw new Error('Failed to decrypt wallet keys');
    }

    // Filter wallets to process
    let walletsToProcess: Keypair[] = specificWallet
      ? decryptedWallets.filter((w: Keypair) => w.publicKey.toString() === specificWallet)
      : decryptedWallets.filter((w: Keypair, idx: number) => {
          const holding = token.walletHoldings.find(h => h.walletAddress === wallets[idx]?.address);
          return holding && holding.balance > 0;
        });

    if (walletsToProcess.length === 0) {
      alert("No wallets with tokens to sell");
      return;
    }

    const results = [];
    let totalTokensSold = 0;
    let totalSolReceived = 0;
    let successful = 0;
    let failed = 0;

    // Helper function to sell from a single wallet
    const sellFromWallet = async (keypair: Keypair, walletIndex: number) => {
      try {
        const walletAddress = keypair.publicKey.toString();
        const holding = token.walletHoldings.find(h => h.walletAddress === walletAddress);
        
        if (!holding || holding.balance === 0) {
          return {
            wallet: walletAddress,
            success: false,
            error: 'No tokens in this wallet'
          };
        }

        const percentage = sellAll ? 100 : parseFloat(sellAmount[sellKey] || '100');
        const amountToSell = (holding.uiBalance * percentage) / 100;

        console.log(`Selling ${amountToSell} ${token.symbol} from wallet ${walletIndex + 1}`);

        // USE PUMPPORTAL FOR PUMP.FUN TOKENS
        if (isPumpToken) {
          try {
            const response = await fetch('https://pumpportal.fun/api/trade-local', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                publicKey: keypair.publicKey.toBase58(),
                action: 'sell',
                mint: tokenMint,
                denominatedInSol: 'false',
                amount: percentage, // Percentage to sell
                slippage: batchSettings.slippageBps / 100,
                priorityFee: 0.0001,
                pool: 'pump'
              }),
            });

            if (!response.ok) {
              throw new Error(`PumpPortal API error: ${response.status}`);
            }

            const data = await response.arrayBuffer();
            const tx = VersionedTransaction.deserialize(new Uint8Array(data));
            tx.sign([keypair]);

            const signature = await connection.sendTransaction(tx, {
              skipPreflight: false,
              maxRetries: 3,
            });

            await connection.confirmTransaction(signature, 'confirmed');

            console.log(`Pump.fun sell successful: ${signature}`);

            return {
              wallet: walletAddress,
              success: true,
              signature,
              tokensSold: amountToSell,
              solReceived: 0 // PumpPortal doesn't return exact SOL received
            };
          } catch (pumpError: any) {
            console.error('PumpPortal sell error:', pumpError);
            return {
              wallet: walletAddress,
              success: false,
              error: `Pump.fun sell failed: ${pumpError.message}`
            };
          }
        } else {
          // USE JUPITER FOR RAYDIUM/DEX TOKENS
          if (!trader) {
            throw new Error('Trading service not initialized');
          }

          const result = await trader.sellTokens(
            keypair,
            tokenMint,
            amountToSell,
            batchSettings.slippageBps
          );

          if (result.success) {
            return {
              wallet: walletAddress,
              success: true,
              signature: result.signature,
              tokensSold: amountToSell,
              solReceived: result.outputAmount || 0
            };
          } else {
            return {
              wallet: walletAddress,
              success: false,
              error: result.error || 'Jupiter sell failed'
            };
          }
        }
      } catch (error: any) {
        console.error(`Sell error for wallet ${walletIndex + 1}:`, error);
        return {
          wallet: keypair.publicKey.toString(),
          success: false,
          error: error.message
        };
      }
    };

    // Process wallets based on batch settings
    if (batchSettings.enabled && walletsToProcess.length > 1) {
      // Batch processing
      for (let i = 0; i < walletsToProcess.length; i += batchSettings.batchSize) {
        const batch = walletsToProcess.slice(i, i + batchSettings.batchSize);
        
        const batchPromises = batch.map((keypair: Keypair, idx: number) => 
          sellFromWallet(keypair, i + idx)
        );
        
        const batchResults = await Promise.allSettled(batchPromises);
        
        batchResults.forEach((result) => {
          if (result.status === 'fulfilled') {
            const data = result.value;
            results.push(data);
            if (data.success) {
              successful++;
              totalTokensSold += data.tokensSold || 0;
              totalSolReceived += data.solReceived || 0;
            } else {
              failed++;
            }
          } else {
            failed++;
            results.push({
              success: false,
              error: result.reason
            });
          }
        });

        // Delay between batches
        if (i + batchSettings.batchSize < walletsToProcess.length && batchSettings.delayBetweenBatches > 0) {
          await new Promise(resolve => setTimeout(resolve, batchSettings.delayBetweenBatches));
        }
      }
    } else {
      // Sequential processing
      for (let i = 0; i < walletsToProcess.length; i++) {
        const keypair = walletsToProcess[i];
        const result = await sellFromWallet(keypair, i);
        results.push(result);
        
        if (result.success) {
          successful++;
          totalTokensSold += result.tokensSold || 0;
          totalSolReceived += result.solReceived || 0;
        } else {
          failed++;
        }

        // Delay between wallets
        if (batchSettings.delayBetweenBatches > 0 && i < walletsToProcess.length - 1) {
          await new Promise(resolve => setTimeout(resolve, batchSettings.delayBetweenBatches));
        }
      }
    }

    // Show results
    if (successful > 0) {
      const mode = batchSettings.enabled ? 'BATCH' : 'SEQUENTIAL';
      const platform = isPumpToken ? 'Pump.fun' : 'Jupiter';
      alert(
        `✅ Successfully sold tokens via ${platform}!\n` +
        `Mode: ${mode}\n` +
        `Tokens sold: ${totalTokensSold.toFixed(4)} ${token.symbol}\n` +
        (totalSolReceived > 0 ? `SOL received: ${totalSolReceived.toFixed(4)} SOL\n` : '') +
        `Wallets: ${successful}/${walletsToProcess.length} successful`
      );

      // Update purchase history
      savePurchaseHistory(tokenMint, {
        totalInvested: 0,
        avgPrice: 0,
        lastSoldPrice: token.currentPrice,
        lastSoldTime: Date.now()
      });

      // Refresh balances
      await fetchAllTokenBalances();
    } else {
      alert(`❌ Failed to sell tokens from all wallets\n\nCheck console for details.`);
    }

    // Log detailed results for debugging
    console.log('Sell results:', {
      platform: isPumpToken ? 'Pump.fun' : 'Jupiter',
      successful,
      failed,
      results
    });

  } catch (error: any) {
    console.error('Error in handleSellToken:', error);
    alert(`❌ Failed to sell tokens: ${error.message || 'Unknown error'}`);
  } finally {
    setSelling(prev => ({ ...prev, [sellKey]: false }));
  }
};

// Additional helper methods you can add to your component:

/**
 * Sell all holdings of a specific token
 */
const sellAllHoldings = async (tokenMint: string) => {
  await handleSellToken(tokenMint, true);
};

/**
 * Emergency sell all tokens from all wallets
 */
const emergencySellAll = async () => {
  if (!window.confirm('⚠️ This will sell ALL tokens from ALL wallets. Are you sure?')) {
  return;
}

  setLoading(true);
  try {
    const allTokens = Object.keys(tokenPortfolio);
    let totalSolReceived = 0;

    for (const tokenMint of allTokens) {
      console.log(`Emergency selling ${tokenPortfolio[tokenMint].symbol}...`);
      await handleSellToken(tokenMint, true);
      // Add small delay between different tokens
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    alert(`✅ Emergency sell completed. Check your SOL balances.`);
    await fetchAllTokenBalances();
  } catch (error: any) {
    console.error('Emergency sell error:', error);
    alert(`❌ Emergency sell failed: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

/**
 * Sell with stop loss monitoring
 */
const sellWithStopLoss = async (
  tokenMint: string,
  stopLossPrice: number,
  checkIntervalMs: number = 5000
) => {
  const token = tokenPortfolio[tokenMint];
  if (!token) return;

  console.log(`Monitoring ${token.symbol} for stop loss at $${stopLossPrice}`);

  const checkPrice = async () => {
    // Fetch current price
    const prices = await fetchTokenPrices([tokenMint]);
    const currentPrice = prices.get(tokenMint)?.price || 0;

    console.log(`Current price: $${currentPrice}, Stop loss: $${stopLossPrice}`);

    if (currentPrice <= stopLossPrice) {
      console.log('Stop loss triggered!');
      await handleSellToken(tokenMint, true);
      return true; // Stop monitoring
    }
    return false; // Continue monitoring
  };

  // Start monitoring
  const intervalId = setInterval(async () => {
    const shouldStop = await checkPrice();
    if (shouldStop) {
      clearInterval(intervalId);
    }
  }, checkIntervalMs);

  // Initial check
  await checkPrice();

  return {
    stop: () => clearInterval(intervalId)
  };
};

/**
 * Sell tokens at target price
 */
const sellAtTargetPrice = async (
  tokenMint: string,
  targetPrice: number,
  maxWaitMs: number = 300000 // 5 minutes default
) => {
  const token = tokenPortfolio[tokenMint];
  if (!token) return;

  console.log(`Waiting for ${token.symbol} to reach $${targetPrice}`);

  const startTime = Date.now();
  
  return new Promise<void>((resolve, reject) => {
    const checkPrice = async () => {
      if (Date.now() - startTime > maxWaitMs) {
        reject(new Error('Maximum wait time exceeded'));
        return;
      }

      // Fetch current price
      const prices = await fetchTokenPrices([tokenMint]);
      const currentPrice = prices.get(tokenMint)?.price || 0;

      console.log(`Current price: $${currentPrice}, Target: $${targetPrice}`);

      if (currentPrice >= targetPrice) {
        console.log('Target price reached!');
        await handleSellToken(tokenMint, true);
        resolve();
      } else {
        // Check again in 5 seconds
        setTimeout(checkPrice, 5000);
      }
    };

    checkPrice();
  });
};

  const refreshPrices = async () => {
    setRefreshing(true);
    await fetchAllTokenBalances();
    setRefreshing(false);
  };

  const copyAddress = (address: string) => navigator.clipboard.writeText(address);
  const openSolscan = (mint: string) => window.open(`https://solscan.io/token/${mint}`, '_blank');

  const formatPrice = (value: number): string => {
    if (value === 0) return '$0.00';
    if (value >= 1) return `$${value.toFixed(2)}`;
    if (value >= 0.01) return `$${value.toFixed(4)}`;
    if (value >= 0.0001) return `$${value.toFixed(6)}`;
    return `$${value.toFixed(8)}`;
  };

  const formatTokenPrice = (value: number): string => {
    if (value === 0) return '$0.00';
    if (value >= 1) return `$${value.toFixed(4)}`;
    if (value >= 0.01) return `$${value.toFixed(6)}`;
    if (value >= 0.0001) return `$${value.toFixed(8)}`;
    if (value >= 0.000001) return `$${value.toFixed(9)}`;
    if (value >= 0.0000001) return `$${value.toFixed(10)}`;
    const decimals = Math.abs(Math.floor(Math.log10(value))) + 2;
    return `$${value.toFixed(Math.min(decimals, 12))}`;
  };

  const sortedTokens = Object.values(tokenPortfolio)
    .filter(t => (filterPositive === null ? true : (filterPositive ? t.pnl >= 0 : t.pnl < 0)))
    .sort((a, b) => {
      switch (sortBy) {
        case 'value': return b.totalValue - a.totalValue;
        case 'pnl': return b.pnl - a.pnl;
        case 'change': return b.pnlPercentage - a.pnlPercentage;
        default: return 0;
      }
    });

  const totals = {
    value: Object.values(tokenPortfolio).reduce((sum, t) => sum + t.totalValue, 0),
    invested: Object.values(tokenPortfolio).reduce((sum, t) => sum + t.totalInvested, 0),
    pnl: Object.values(tokenPortfolio).reduce((sum, t) => sum + t.pnl, 0),
    pnlPercentage: 0
  };
  if (totals.invested > 0) {
    totals.pnlPercentage = ((totals.value - totals.invested) / totals.invested) * 100;
  }

  if (!isConnected) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 md:p-8 border border-gray-700 mb-8">
        <div className="text-center py-12">
          <Wallet className="w-12 h-12 mx-auto mb-3 text-gray-400 opacity-50" />
          <p className="text-gray-400 mb-4">Please connect your wallet to manage token portfolio</p>
        </div>
      </div>
    );
  }

  if (bundles.length === 0 && !loading) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 md:p-8 border border-gray-700 mb-8">
        <div className="text-center py-12">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-400 opacity-50" />
          <p className="text-gray-400 mb-4">No bundles found</p>
          <p className="text-sm text-gray-500">Create a bundle in the Tools section first</p>
          <button
            onClick={loadUserBundles}
            className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            Refresh Bundles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-4 md:p-8 border border-gray-700 mb-8">
      {/* Bundle Selector */}
      {bundles.length > 0 && (
        <div className="mb-6 bg-gray-900/50 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
            <label className="text-sm font-medium text-gray-400">Active Bundle</label>
            <button
              onClick={loadUserBundles}
              className="text-xs text-purple-400 hover:text-purple-300"
            >
              Refresh Bundles
            </button>
          </div>
          <select
            value={selectedBundleId || ''}
            onChange={(e) => handleBundleSelect(e.target.value)}
            className="w-full bg-gray-700 text-white rounded-lg px-3 py-2"
          >
            {bundles.map(bundle => (
              <option key={bundle.id} value={bundle.id}>
                {bundle.name} - {bundle.wallets.length} wallets 
                ({bundle.wallets.filter((w: any) => w.funded).length} funded, 
                {bundle.totalBalance.toFixed(4)} SOL)
              </option>
            ))}
          </select>
          {currentBundle && (
            <div className="mt-2 text-xs text-gray-500">
              Bundle ID: {currentBundle.id.slice(0, 12)}...
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <h2 className="text-xl md:text-2xl font-bold flex items-center flex-wrap gap-2">
          <Package className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
          <span>Token Portfolio Manager</span>
          {batchSettings.enabled && (
            <span className="text-xs bg-green-600/20 text-green-400 px-2 py-1 rounded">
              BATCH MODE
            </span>
          )}
        </h2>
        
        {/* Desktop Actions */}
        <div className="hidden lg:flex space-x-2">
          <button
            onClick={() => setShowBatchSettings(!showBatchSettings)}
            className="px-3 md:px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            <Settings className="w-4 h-4" />
            <span>Batch Settings</span>
          </button>
          <button
            onClick={() => setShowPrices(!showPrices)}
            className="px-3 md:px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            {showPrices ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showPrices ? 'Hide' : 'Show'} Prices</span>
          </button>
          <button
            onClick={refreshPrices}
            disabled={refreshing}
            className="px-3 md:px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          className="lg:hidden px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile Actions Menu */}
      {showMobileMenu && (
        <div className="lg:hidden mb-4 space-y-2">
          <button
            onClick={() => {
              setShowBatchSettings(!showBatchSettings);
              setShowMobileMenu(false);
            }}
            className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            <Settings className="w-4 h-4" />
            <span>Batch Settings</span>
          </button>
          <button
            onClick={() => {
              setShowPrices(!showPrices);
              setShowMobileMenu(false);
            }}
            className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            {showPrices ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showPrices ? 'Hide' : 'Show'} Prices</span>
          </button>
          <button
            onClick={() => {
              refreshPrices();
              setShowMobileMenu(false);
            }}
            disabled={refreshing}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      )}

      {/* Batch Settings Panel */}
      {showBatchSettings && (
        <div className="mb-6 bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
          <h3 className="font-medium mb-3 text-purple-400 flex items-center">
            <Zap className="w-4 h-4 mr-2" />
            Batch Selling Configuration
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={batchSettings.enabled}
                  onChange={(e) => saveBatchSettings({ ...batchSettings, enabled: e.target.checked })}
                  className="w-4 h-4 text-purple-600"
                />
                <span className="text-sm">Enable Batch Mode</span>
              </label>
              <p className="text-xs text-gray-400 mt-1">Process multiple wallets in parallel</p>
            </div>

            <div>
              <label className="block text-sm mb-1">Batch Size</label>
              <input
                type="number"
                min="1"
                max="50"
                value={batchSettings.batchSize}
                onChange={(e) => saveBatchSettings({ ...batchSettings, batchSize: parseInt(e.target.value) || 10 })}
                className="w-full px-2 py-1 bg-gray-700 rounded text-sm"
                disabled={!batchSettings.enabled}
              />
              <p className="text-xs text-gray-400 mt-1">Wallets per batch</p>
            </div>

            <div>
              <label className="block text-sm mb-1">Batch Delay (ms)</label>
              <input
                type="number"
                min="0"
                max="5000"
                step="100"
                value={batchSettings.delayBetweenBatches}
                onChange={(e) => saveBatchSettings({
                  ...batchSettings,
                  delayBetweenBatches: parseInt(e.target.value) || 100
                })}
                className="w-full px-2 py-1 bg-gray-700 rounded text-sm"
                disabled={!batchSettings.enabled}
              />
              <p className="text-xs text-gray-400 mt-1">Delay between batches</p>
            </div>

            <div>
              <label className="block text-sm mb-1">Slippage %</label>
              <input
                type="number"
                min="0.1"
                max="50"
                step="0.1"
                value={batchSettings.slippageBps / 100}
                onChange={(e) => saveBatchSettings({
                  ...batchSettings,
                  slippageBps: (parseFloat(e.target.value) || 1) * 100
                })}
                className="w-full px-2 py-1 bg-gray-700 rounded text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">Max slippage tolerance</p>
            </div>
          </div>

          <div className="mt-3 text-xs text-gray-400">
            <Info className="w-3 h-3 inline mr-1" />
            Batch mode processes {batchSettings.batchSize} wallets simultaneously for faster execution.
            {batchSettings.enabled &&
              ` Total time estimate: ~${Math.ceil(wallets.length / batchSettings.batchSize) * (batchSettings.delayBetweenBatches / 1000)}s for ${wallets.length} wallets.`}
          </div>
        </div>
      )}

      {/* Portfolio Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-900/50 rounded-lg p-3 md:p-4">
          <div className="text-xs md:text-sm text-gray-400">Total Value</div>
          <div className="text-lg md:text-2xl font-bold text-white">
            {showPrices ? formatPrice(totals.value) : '•••••'}
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 md:p-4">
          <div className="text-xs md:text-sm text-gray-400">Total Invested</div>
          <div className="text-lg md:text-2xl font-bold text-purple-400">
            {showPrices ? formatPrice(totals.invested) : '•••••'}
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 md:p-4">
          <div className="text-xs md:text-sm text-gray-400">Total PnL</div>
          <div className={`text-lg md:text-2xl font-bold ${totals.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {showPrices ? (<>{totals.pnl >= 0 ? '+' : ''}{formatPrice(Math.abs(totals.pnl))}</>) : '•••••'}
          </div>
        </div>
        <div className="bg-gray-900/50 rounded-lg p-3 md:p-4">
          <div className="text-xs md:text-sm text-gray-400">PnL %</div>
          <div className={`text-lg md:text-2xl font-bold flex items-center ${totals.pnlPercentage >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {showPrices ? (
              <>
                {totals.pnlPercentage >= 0 ? <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5 mr-1" /> : <ArrowDownRight className="w-4 h-4 md:w-5 md:h-5 mr-1" />}
                {Math.abs(totals.pnlPercentage).toFixed(2)}%
              </>
            ) : '•••••'}
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
        <div className="flex space-x-2">
          <button
            onClick={() => setFilterPositive(null)}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${filterPositive === null ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
          >
            All
          </button>
          <button
            onClick={() => setFilterPositive(true)}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${filterPositive === true ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'}`}
          >
            Profit
          </button>
          <button
            onClick={() => setFilterPositive(false)}
            className={`px-3 py-1 rounded-lg transition-colors text-sm ${filterPositive === false ? 'bg-red-600' : 'bg-gray-700 hover:bg-gray-600'}`}
          >
            Loss
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-400">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="bg-gray-700 text-white rounded-lg px-3 py-1 text-sm"
          >
            <option value="value">Value</option>
            <option value="pnl">PnL $</option>
            <option value="change">PnL %</option>
          </select>
        </div>
      </div>

      {/* Token List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          <span className="ml-3 text-gray-400">Loading token portfolio...</span>
        </div>
      ) : sortedTokens.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No tokens found in bundler wallets</p>
          <p className="text-sm mt-2">Try purchasing some tokens using the Jupiter Purchase feature</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedTokens.map((token) => (
            <div key={token.mint} className="bg-gray-900/50 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex items-center space-x-3">
                    {token.image ? (
                      <img src={token.image} alt={token.symbol} className="w-8 h-8 md:w-10 md:h-10 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-700 rounded-full flex items-center justify-center">
                        <Coins className="w-5 h-5 md:w-6 md:h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-bold text-base md:text-lg">{token.symbol}</h3>
                        <span className="text-xs md:text-sm text-gray-400 hidden sm:inline">{token.name}</span>
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500 font-mono">
                          {token.mint.slice(0, 6)}...{token.mint.slice(-4)}
                        </span>
                        <button onClick={() => copyAddress(token.mint)} className="text-gray-400 hover:text-white transition-colors">
                          <Copy className="w-3 h-3" />
                        </button>
                        <button onClick={() => openSolscan(token.mint)} className="text-gray-400 hover:text-white transition-colors">
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-xl md:text-2xl font-bold">
                      {showPrices ? formatPrice(token.totalValue) : '•••••'}
                    </div>
                    <div className={`text-xs md:text-sm ${token.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {showPrices ? (
                        <>
                          {token.pnl >= 0 ? '+' : ''}{formatPrice(Math.abs(token.pnl))} ({token.pnlPercentage >= 0 ? '+' : ''}{token.pnlPercentage.toFixed(2)}%)
                        </>
                      ) : '•••••'}
                    </div>
                  </div>
                </div>

                {/* Token Stats - Mobile Responsive */}
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-4 mt-4">
                  <div>
                    <div className="text-xs text-gray-400">Balance</div>
                    <div className="text-xs md:text-sm font-medium">
                      {(token.totalBalance / Math.pow(10, token.decimals)).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Price</div>
                    <div className="text-xs md:text-sm font-medium">
                      {showPrices ? formatTokenPrice(token.currentPrice) : '•••'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Avg Buy</div>
                    <div className="text-xs md:text-sm font-medium">
                      {showPrices ? formatTokenPrice(token.avgBuyPrice) : '•••'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Invested</div>
                    <div className="text-xs md:text-sm font-medium">
                      {showPrices ? formatPrice(token.totalInvested) : '•••'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">Wallets</div>
                    <div className="text-xs md:text-sm font-medium">{token.walletHoldings.length}</div>
                  </div>
                  <div className="flex items-center space-x-1 lg:space-x-2 col-span-3 lg:col-span-1">
                    <button
                      onClick={() => setExpandedWallets(prev => ({ ...prev, [token.mint]: !prev[token.mint] }))}
                      className="px-2 lg:px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded text-xs lg:text-sm flex items-center space-x-1"
                    >
                      {expandedWallets[token.mint] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      <span>Wallets</span>
                    </button>
                    <button
                      onClick={() => handleSellToken(token.mint, true)}
                      disabled={selling[token.mint]}
                      className="px-2 lg:px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs lg:text-sm flex items-center space-x-1 disabled:opacity-50"
                    >
                      {selling[token.mint] ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : batchSettings.enabled ? (
                        <Zap className="w-3 h-3" />
                      ) : (
                        <Trash2 className="w-3 h-3" />
                      )}
                      <span>Sell All</span>
                    </button>
                  </div>
                </div>

                {/* Wallet Holdings */}
                {expandedWallets[token.mint] && (
                  <div className="mt-4 border-t border-gray-700 pt-4">
                    <div className="text-sm font-medium mb-2 text-gray-400">Wallet Holdings</div>
                    <div className="space-y-2">
                      {token.walletHoldings.map((holding) => (
                        <div key={holding.walletAddress} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-gray-800/50 rounded-lg p-3">
                          <div className="flex items-center space-x-3">
                            <span className="text-xs bg-purple-900/50 text-purple-400 px-2 py-1 rounded">
                              #{holding.walletIndex + 1}
                            </span>
                            <div>
                              <div className="font-mono text-xs">
                                {holding.walletAddress.slice(0, 8)}...{holding.walletAddress.slice(-6)}
                              </div>
                              <div className="text-xs text-gray-400">
                                {holding.uiBalance.toLocaleString()} tokens
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center space-x-3 w-full sm:w-auto">
                            <div className="text-right flex-grow sm:flex-grow-0">
                              <div className="text-sm font-medium">
                                {showPrices ? formatPrice(holding.value) : '•••'}
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                min="1"
                                max="100"
                                value={sellAmount[`${token.mint}-${holding.walletAddress}`] || '100'}
                                onChange={(e) => setSellAmount(prev => ({
                                  ...prev,
                                  [`${token.mint}-${holding.walletAddress}`]: e.target.value
                                }))}
                                className="w-16 px-2 py-1 bg-gray-700 rounded text-xs"
                                placeholder="%"
                              />
                              <button
                                onClick={() => handleSellToken(token.mint, false, holding.walletAddress)}
                                disabled={selling[`${token.mint}-${holding.walletAddress}`]}
                                className="px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded text-xs disabled:opacity-50"
                              >
                                {selling[`${token.mint}-${holding.walletAddress}`] ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  'Sell'
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
        <h3 className="font-medium mb-2 text-blue-400 flex items-center text-sm md:text-base">
          <Info className="w-4 h-4 mr-2" />
          Portfolio Features
        </h3>
        <ul className="text-xs md:text-sm text-gray-300 space-y-1 list-disc list-inside">
          <li>View all tokens held across your bundler wallets with real-time prices</li>
          <li>Track PnL (Profit & Loss) for each token based on purchase history</li>
          <li className="text-purple-400">
            <strong>Batch Mode:</strong> Process multiple wallet sells in parallel for faster execution
          </li>
          <li>Configure batch size and delays for optimal performance</li>
          <li>Sell tokens individually per wallet or all at once</li>
          <li>Filter tokens by profit/loss status</li>
          <li>Sort by value, PnL amount, or PnL percentage</li>
        </ul>
      </div>
    </div>
  );
};

export default BundlerTokenPortfolioManager;